import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatDetail } from "@/lib/api";
import {
  Users, CheckCircle2, Send, Loader2, Handshake, Gift, Globe2,
  KeyRound, Megaphone, LogOut, UserCheck,
} from "lucide-react";
import MarketingHub from "@/components/MarketingHub";

const COUNTRIES = ["South Africa","Zimbabwe","Zambia","Botswana","Namibia","Mozambique","United Kingdom","United States","Canada","Australia","Germany","Other"];

const AGENT_CACHE = "mv_agent_session";

export default function AgentPortalPage() {
  // Portal main menu: Apply · Marketing Hub
  const [tab, setTab] = useState("apply");

  // Agent authentication via referral code
  const [agent, setAgent] = useState(() => {
    try { return JSON.parse(localStorage.getItem(AGENT_CACHE) || "null"); }
    catch { return null; }
  });

  // If we arrive with #marketing hash, default to that tab (opens code prompt if not signed in)
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash === "#marketing") {
      setTab("marketing");
    }
  }, []);

  return (
    <main data-testid="agent-portal-page">
      {/* HERO */}
      <section className="mv-green-bg text-white py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ background: "radial-gradient(600px 300px at 20% 0%, var(--mv-gold), transparent 60%)" }}/>
        <div className="max-w-5xl mx-auto px-4 md:px-8 relative">
          <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: "var(--mv-gold)" }}>§ Agent Portal</div>
          <h1 className="font-display text-4xl md:text-6xl mt-4 leading-[0.98]">
            Become a MAVULA agent. <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>Open doors in your city.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-white/85 text-[15.5px] leading-relaxed">
            Are you already trusted in your diaspora community? Church group? Village? Help MAVULA reach real families —
            and earn a fair commission on every registration you refer. No cold-calling. No pressure sales. Only dignity.
          </p>
        </div>
      </section>

      {/* MAIN MENU */}
      <nav className="border-b mv-hairline bg-white sticky top-[64px] z-30" data-testid="agent-portal-menu">
        <div className="max-w-6xl mx-auto px-4 md:px-8 flex flex-wrap items-center gap-2 py-3">
          <MenuBtn active={tab === "apply"} onClick={() => setTab("apply")} testid="agent-menu-apply" icon={<Users size={15}/>}>
            Apply
          </MenuBtn>
          <MenuBtn active={tab === "marketing"} onClick={() => setTab("marketing")} testid="agent-menu-marketing" icon={<Megaphone size={15}/>}>
            📢 Marketing Hub
          </MenuBtn>
          <div className="ml-auto text-[12px] flex items-center gap-3" style={{ color: "var(--mv-muted)" }}>
            {agent ? (
              <span className="inline-flex items-center gap-2" data-testid="agent-session">
                <UserCheck size={14} className="mv-green-text"/>
                <span className="font-mono">{agent.agent_code}</span>
                <button
                  type="button"
                  onClick={() => { localStorage.removeItem(AGENT_CACHE); setAgent(null); toast.success("Signed out."); }}
                  className="inline-flex items-center gap-1 hover:underline"
                  data-testid="agent-signout-btn"
                >
                  <LogOut size={13}/> Sign out
                </button>
              </span>
            ) : null}
          </div>
        </div>
      </nav>

      {tab === "apply" && <ApplyPanel/>}
      {tab === "marketing" && (
        agent ? <MarketingHub agent={agent}/> : <AgentGate onAuth={setAgent}/>
      )}

      {/* WHY (shown on Apply tab only for context) */}
      {tab === "apply" && (
        <section className="py-14 md:py-20 border-t mv-hairline">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ What you get</span></div>
            <h2 className="font-display text-3xl md:text-5xl mv-green-text mt-3 leading-[1.05]">
              Fair rewards, <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>real dignity.</span>
            </h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { i: <Gift size={22}/>, t: "Fair commission", d: "Paid on every registration that becomes a titled owner. Transparent, tracked in your Personal Log." },
                { i: <Handshake size={22}/>, t: "Full training", d: "Free online training via Pula Learn. You never sell — you educate, and MAVULA follows up." },
                { i: <Globe2 size={22}/>, t: "Serve your community", d: "Choose the country/diaspora you already belong to. We equip you with tools + verified answers." },
              ].map((c) => (
                <div key={c.t} className="mv-card p-6">
                  <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4 mv-green-text" style={{ background: "rgba(212,175,55,0.16)" }}>{c.i}</div>
                  <div className="font-display text-xl mv-green-text">{c.t}</div>
                  <p className="mt-2 text-[14px]" style={{ color: "var(--mv-muted)" }}>{c.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}

function MenuBtn({ active, onClick, children, testid, icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13.5px] transition ${active ? "mv-green-bg text-white" : "hover:bg-black/[0.04]"}`}
      style={active ? {} : { color: "var(--mv-ink)" }}
      data-testid={testid}
    >
      {icon}{children}
    </button>
  );
}

// --- Agent code gate ---
function AgentGate({ onAuth }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) { toast.error("Enter your agent code."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/agents/verify-code", { code: code.trim() });
      localStorage.setItem(AGENT_CACHE, JSON.stringify(data.agent));
      onAuth(data.agent);
      toast.success(`Welcome, ${data.agent.full_name || "agent"}.`);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <section className="py-14 md:py-20" data-testid="agent-gate">
      <div className="max-w-md mx-auto px-4 md:px-8">
        <div className="mv-card p-8">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 mv-green-text" style={{ background: "rgba(212,175,55,0.16)" }}>
            <KeyRound size={20}/>
          </div>
          <div className="font-display text-2xl mv-green-text">Approved agents only</div>
          <p className="mt-2 text-[13.5px]" style={{ color: "var(--mv-muted)" }}>
            Enter the <strong>MV-AGT-XXXXX</strong> code issued to you after approval to unlock the Marketing Hub.
          </p>
          <form onSubmit={submit} className="mt-5 space-y-3">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="MV-AGT-XXXXX"
              className="w-full px-3 py-2 rounded-xl border mv-hairline text-[14px] font-mono tracking-wider"
              data-testid="agent-code-input"
              autoFocus
            />
            <button type="submit" disabled={busy} className="btn-green w-full" data-testid="agent-code-submit">
              {busy ? <Loader2 className="animate-spin" size={16}/> : <KeyRound size={16}/>}
              {busy ? "Verifying…" : "Unlock Marketing Hub"}
            </button>
          </form>
          <p className="mt-4 text-[12px]" style={{ color: "var(--mv-muted)" }}>
            Not an agent yet? Submit an application under the <em>Apply</em> tab. We'll respond within 5 working days.
          </p>
        </div>
      </div>
    </section>
  );
}

// --- Application form (unchanged) ---
function ApplyPanel() {
  const [form, setForm] = useState({
    full_name: "", country_current: "", country_serving: "",
    phone: "", email: "", experience: "", languages: "", why_join: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.country_current) {
      toast.error("Please share at least your name and where you live.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/agent-applications", form);
      setDone(true);
      toast.success("Application received. We'll respond within 5 working days.");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <section className="py-14 md:py-20">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Apply</span></div>
        <h2 className="font-display text-3xl md:text-4xl mv-green-text mt-3">
          Tell us about yourself.
        </h2>
        {done ? (
          <div className="mt-8 mv-card p-8 flex items-start gap-4" style={{ background: "linear-gradient(180deg, #fff, rgba(37,211,102,0.06))" }} data-testid="agent-portal-success">
            <CheckCircle2 size={26} style={{ color: "#0A7A2C" }}/>
            <div>
              <div className="font-display text-2xl mv-green-text">Application received.</div>
              <p className="mt-2 text-[14.5px]" style={{ color: "var(--mv-muted)" }}>
                Our team will review within 5 working days. If we invite you forward, you'll receive an onboarding email + free Pula Learn access.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 mv-card p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="agent-portal-form">
            <Field label="Full name *" value={form.full_name} onChange={set("full_name")} testid="ag-name"/>
            <Sel   label="Country you live in *" value={form.country_current} onChange={set("country_current")} options={["", ...COUNTRIES]} testid="ag-current"/>
            <Sel   label="Country you serve" value={form.country_serving} onChange={set("country_serving")} options={["", ...COUNTRIES]} testid="ag-serving"/>
            <Field label="Phone / WhatsApp" value={form.phone} onChange={set("phone")} testid="ag-phone"/>
            <Field label="Email" value={form.email} onChange={set("email")} testid="ag-email"/>
            <Field label="Languages you speak" value={form.languages} onChange={set("languages")} placeholder="e.g. Shona, English, Portuguese" testid="ag-langs"/>
            <TA    label="Community experience" rows={3} value={form.experience} onChange={set("experience")} className="md:col-span-2" testid="ag-exp"/>
            <TA    label="Why you want to join MAVULA" rows={3} value={form.why_join} onChange={set("why_join")} className="md:col-span-2" testid="ag-why"/>
            <div className="md:col-span-2 pt-3 border-t mv-hairline flex justify-between items-center">
              <div className="text-[12.5px] font-accent italic" style={{ color: "var(--mv-muted)" }}>
                We only invite forward applicants who complete the full form.
              </div>
              <button type="submit" disabled={busy} className="btn-green" data-testid="ag-submit">
                {busy ? <Loader2 className="animate-spin" size={16}/> : <Send size={16}/>}
                {busy ? "Sending…" : "Submit application"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

const cls = "mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]";
function Field({ label, value, onChange, placeholder, className = "", testid }) {
  return <label className={`block ${className}`}>
    <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>{label}</span>
    <input value={value} onChange={onChange} placeholder={placeholder} className={cls} data-testid={testid}/>
  </label>;
}
function TA({ label, value, onChange, rows = 3, className = "", testid }) {
  return <label className={`block ${className}`}>
    <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>{label}</span>
    <textarea value={value} onChange={onChange} rows={rows} className={cls} data-testid={testid}/>
  </label>;
}
function Sel({ label, value, onChange, options, testid }) {
  return <label className="block">
    <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>{label}</span>
    <select value={value} onChange={onChange} className={cls} data-testid={testid}>
      {options.map((o) => <option key={o || "any"} value={o}>{o || "— Select —"}</option>)}
    </select>
  </label>;
}
